import { getUserFromLocalStorage } from '@/utils/user';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
// import WorkerNotification from './WorkerNotification.class';
import { notification } from 'antd';
import { NotificationHubConfig } from './Notification.config';
import { USER_NOTIFICATION_LIST_KEY } from '@/core/domain/communication/userNotification/userNotification.hooks';
import { useQueryChanel } from '../QueryChanelProvider';
import { ChanelNotificationData } from './Notification.types';
import { UserNotification } from '@/core/domain/communication/userNotification/userNotification.types';
import { queryClient } from '@/Root';
import { useDispatch } from 'react-redux';
import { authOperations } from '@/state/ducks/auth';
import { useHistory } from 'react-router-dom';

const NotificationContext = createContext(
  {} as { notificationCount: number; userNotification?: UserNotification },
);

const scriptUrl = '/websocket-worker.js';

export const useNotification = () => useContext(NotificationContext);

export default function NotificationProvider(
  props: PropsWithChildren<unknown>,
) {
  const [socketWorker, setSocketWorker] =
    useState<ServiceWorkerRegistration | null>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [userNotification, setUserNotification] = useState<UserNotification>();

  const queryChanel = useQueryChanel();
  const dispatch = useDispatch();
  const history = useHistory();

  const registerWebsocket = useCallback(async () => {
    const session = getUserFromLocalStorage();

    if (!socketWorker && session.accessToken && 'serviceWorker' in navigator) {
      try {
        await navigator.serviceWorker?.register(scriptUrl, {
          updateViaCache: 'none',
        });
      } catch (error) {
        console.error('>> Registration Error: ', error);
      }
    }
  }, [socketWorker]);

  const connectWebsocket = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator) || socketWorker) return;

      const worker = await navigator.serviceWorker?.ready;

      if (worker.active) {
        const session = getUserFromLocalStorage();

        if (!session.accessToken || !session.userId) return;

        const urlConnection = `${process.env.REACT_APP_API_URL}/communication/ws`;

        worker.active?.postMessage({
          type: 'CONNECT',
          accessToken: session.accessToken,
          userId: session.userId,
          matrixId: session.matrixId,
          urlConnection,
        });

        setSocketWorker(worker);
      }
    } catch (error) {
      console.error('>> Websocket connection:', error);
    }
  }, [socketWorker]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const interval = setInterval(async () => {
      const userData = getUserFromLocalStorage();

      if (userData.accessToken) {
        await registerWebsocket();
        await connectWebsocket();
        clearInterval(interval);
      }
    }, 1000);

    const handleMessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case 'NEW_MESSAGE':
          showNotification(event.data.payload);
          break;
        case 'FORCE_LOGOUT':
          forceLogout(event.data.payload);
          break;
        default:
          break;
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, [registerWebsocket, connectWebsocket]);

  const showNotification = async (data: ChanelNotificationData) => {
    const notifications = data?.notifications || [];
    const lastNotification = notifications?.at(-1);
    const unreadNotificationsCount = data.unreadNotificationsCount;

    setNotificationCount(unreadNotificationsCount);
    setUserNotification(lastNotification);

    if (lastNotification) {
      queryChanel({ invalidateQueries: [[USER_NOTIFICATION_LIST_KEY]] });
    }

    const { storage } = new NotificationHubConfig();

    if (storage.balloonEnabled && lastNotification) {
      notification.close('notification-hub');
      notification.info({
        key: 'notification-hub',
        message: 'Notificação',
        description: lastNotification?.message,
      });
    }

    if (storage.audioEnabled && lastNotification) {
      try {
        const audio = new Audio('/sounds/notification.mp3');
        if (audio) await audio.play();
      } catch {}
    }

    if (storage.notificationPermissionEnabled && lastNotification) {
      // const wsNotification = new WorkerNotification();
      // await wsNotification.showNotification(
      //   lastNotification,
      //   session.accessToken,
      // );
    }
  };

  const forceLogout = async (data: { forceLogout: true; matrixId: number }) => {
    const session = getUserFromLocalStorage();

    if (!data.forceLogout || data.matrixId !== Number(session.matrixId)) return;

    queryClient.clear();
    dispatch(authOperations.handleLogout());
    history.replace(`/login?redirect=${window.location.pathname}`);
  };

  return (
    <NotificationContext.Provider
      value={{ notificationCount, userNotification }}
    >
      {props.children}
    </NotificationContext.Provider>
  );
}
